import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";

import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  makeStyles,
  Box,
  Button,
  CircularProgress,
} from "@material-ui/core";

import Api from "lib/api/httpRequestHelper";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import HeaderClientes from "components/_refactor/headers/clients";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import useStructureQueries from "hooks/useStructureQueries";

import componentStyles from "assets/theme/views";
import themeColorsDesingSystem from "assets/theme/ds-colors";

import {
  getSessionUserProfiles,
  userIsRepresentante,
  sessionUserIsBackoffice,
} from "utils/profile-classification";

import {
  DEFAULT_REPRESENTATIVE_LIST_FILTERS,
  REPRESENTATIVE_PAGE_SIZE,
} from "../shared/utils/representative-helpers";
import { buildClientePagedQuery } from "../shared/utils/representative-api-params";

import RepresentativeFilters from "../shared/components/representative-filters";
import RepresentativeSelectionList from "../shared/components/representative-selection-list";

const useStyles = makeStyles(componentStyles);

export default function PublicarVitrine() {
  const [isLoading, setIsLoading] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filters, setFilters] = useState({
    ...DEFAULT_REPRESENTATIVE_LIST_FILTERS,
  });
  const [grupoEconomicoOptions, setGrupoEconomicoOptions] = useState([]);
  const [razaoSocialOptions, setRazaoSocialOptions] = useState([]);
  const [representantes, setRepresentantes] = useState([]);
  const [idsRepresentanteJaPublicaram, setIdsRepresentanteJaPublicaram] =
    useState(new Set());
  const [isLoadingRepresentantes, setIsLoadingRepresentantes] = useState(false);
  const [selectedRepresentante, setSelectedRepresentante] = useState(null);

  const history = useHistory();
  const searchParams = new URLSearchParams(window.location.search);
  const classes = useStyles();
  const id = searchParams.get("id");
  const snackbar = useSnackbar();
  const { getEconomicGroups, getStores } = useStructureQueries();

  // Mesma checagem estrutural (TiposPessoa, exclui Supervisor) usada em
  // visualizar-vitrines/page.js — antes esta tela comparava nome de perfil em
  // texto livre (REPRESENTANTE/REPRESENTANTE_ADM), divergindo daquela.
  // Backoffice sempre vence: se o perfil também carregar a flag de representante (dual-role, ex.
  // usuário CRC), não pode cair no caminho de representante, senão o seletor "Publicar em nome
  // do representante" fica escondido e o idPessoaRepresentante nunca é enviado ao backend.
  const isRepresentative =
    !sessionUserIsBackoffice() && userIsRepresentante(getSessionUserProfiles());

  const idPessoaRepresentante = isRepresentative
    ? null
    : (selectedRepresentante?.IdPessoaRepresentante ?? null);

  const representantesDisponiveis = useMemo(
    () =>
      representantes.map((r) => ({
        ...r,
        DisplayLabel: idsRepresentanteJaPublicaram.has(r.IdPessoaRepresentante)
          ? `${r.NomeRepresentante ?? ""} — ${r.CnpjCpfRepresentante ?? ""} (já publicou)`
          : `${r.NomeRepresentante ?? ""} — ${r.CnpjCpfRepresentante ?? ""}`,
      })),
    [representantes, idsRepresentanteJaPublicaram],
  );

  const selectedIds = useMemo(
    () => new Set(selectedCustomers),
    [selectedCustomers],
  );

  const loadFilterOptions = useCallback(async () => {
    try {
      const [groupsRes, storesRes] = await Promise.all([
        getEconomicGroups({}),
        getStores({}),
      ]);

      setGrupoEconomicoOptions(
        (groupsRes?.data ?? []).map((g) => ({
          Id: g.Id ?? g.id,
          Nome: g.Nome ?? g.nome,
        })),
      );
      setRazaoSocialOptions(
        (storesRes?.data ?? []).map((s) => ({
          Id: s.Id ?? s.id,
          Nome: (s.Nome ?? s.nome ?? "").trim(),
        })),
      );
    } catch {
      setGrupoEconomicoOptions([]);
      setRazaoSocialOptions([]);
    }
  }, [getEconomicGroups, getStores]);

  const loadRepresentantes = useCallback(async () => {
    if (!id) return;

    setIsLoadingRepresentantes(true);
    try {
      const response = await Api.get(
        "virtual-showcase-b2b-detail",
        "showcaseB2B",
        `?idVitrineVirtualB2b=${id}`,
        true,
      );

      const result = response?.Result;
      setRepresentantes(result?.Representantes ?? []);
      setIdsRepresentanteJaPublicaram(
        new Set((result?.Clientes ?? []).map((c) => c.IdPessoaRepresentante)),
      );
    } catch (error) {
      console.error(error);
      setRepresentantes([]);
      setIdsRepresentanteJaPublicaram(new Set());
      snackbar.showMessage(
        "Erro ao buscar representantes da vitrine. Tente novamente.",
        { severity: "error" },
      );
    } finally {
      setIsLoadingRepresentantes(false);
    }
  }, [id, snackbar]);

  const fetchSeqRef = useRef(0);

  const fetchPage = useCallback(
    async (pageToLoad, filtersToUse) => {
      const seq = ++fetchSeqRef.current;

      if (!id || (!isRepresentative && !idPessoaRepresentante)) {
        setItems([]);
        setTotalItems(0);
        setTotalPages(0);
        return;
      }

      setIsLoading(true);
      try {
        const query = buildClientePagedQuery(filtersToUse, {
          idVitrineVirtualB2b: id,
          page: pageToLoad,
          pageSize: REPRESENTATIVE_PAGE_SIZE,
          idPessoaRepresentante,
        });

        const response = await Api.get(
          "list-b2b-showcase-publish-clients",
          "showcaseB2B",
          `?${query}`,
          true,
        );

        // Ignora respostas de buscas antigas que resolveram depois de uma mais recente
        // (ex.: digitação rápida no CNPJ ou troca de página antes da resposta anterior voltar).
        if (seq !== fetchSeqRef.current) return;

        const result = response?.Result;
        const list = (result?.Lista ?? result?.Result ?? []).map((cliente) => ({
          ...cliente,
          __selectionId: cliente.IdPessoaLoja,
        }));

        setItems(list);
        setTotalItems(result?.TotalRegistros ?? 0);
        setTotalPages(result?.TotalDePaginas ?? 0);
        setPage(result?.Pagina ?? pageToLoad);
      } catch (error) {
        if (seq !== fetchSeqRef.current) return;
        console.error(error);
        setItems([]);
        setTotalItems(0);
        setTotalPages(0);
        snackbar.showMessage("Erro ao buscar clientes. Tente novamente.", {
          severity: "error",
        });
      } finally {
        if (seq === fetchSeqRef.current) setIsLoading(false);
      }
    },
    [id, snackbar, isRepresentative, idPessoaRepresentante],
  );

  const handleFiltersChange = useCallback((updates) => {
    setFilters((prev) => ({ ...prev, ...updates }));
    setPage(0);
  }, []);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    if (!isRepresentative) loadRepresentantes();
  }, [isRepresentative, loadRepresentantes]);

  useEffect(() => {
    fetchPage(page, filters);
  }, [fetchPage, page, filters]);

  const handleRepresentanteChange = useCallback((value) => {
    setSelectedRepresentante(value);
    setSelectedCustomers([]);
    setPage(0);
  }, []);

  const isCustomerSelectable = useCallback((c) => c.Disponivel !== false, []);

  const handleToggleItem = useCallback((item) => {
    if (item?.Disponivel === false) return;
    const clienteId = item.__selectionId;
    setSelectedCustomers((prev) =>
      prev.includes(clienteId)
        ? prev.filter((pid) => pid !== clienteId)
        : [...prev, clienteId],
    );
  }, []);

  const selectVisible = useCallback((visibleIds) => {
    setSelectedCustomers((prev) =>
      Array.from(new Set([...prev, ...visibleIds])),
    );
  }, []);

  const deselectVisible = useCallback((visibleIds) => {
    const hide = new Set(visibleIds);
    setSelectedCustomers((prev) => prev.filter((pid) => !hide.has(pid)));
  }, []);

  const handleSelectAllResults = useCallback(async () => {
    if (!id || (!isRepresentative && !idPessoaRepresentante)) return;

    // Uma chamada só (retornarTodos), sem loop de páginas — mesmo padrão de
    // incluir-produtos/page.js:handleSelectAllResults. Sem teto de seleção aqui: o backend
    // (ListarClientesCarteiraPaginado) já tem seu próprio teto de segurança (20000, não é
    // limite de negócio) pra retornarTodos.
    const query = buildClientePagedQuery(filters, {
      idVitrineVirtualB2b: id,
      idPessoaRepresentante,
      retornarTodos: true,
    });

    const response = await Api.get(
      "list-b2b-showcase-publish-clients",
      "showcaseB2B",
      `?${query}`,
      true,
    );

    const result = response?.Result;
    const list = result?.Lista ?? result?.Result ?? [];
    const allIds = list
      .filter((cliente) => cliente.Disponivel !== false)
      .map((cliente) => cliente.IdPessoaLoja)
      .filter((pid) => pid != null);

    setSelectedCustomers((prev) => Array.from(new Set([...prev, ...allIds])));
  }, [filters, id, isRepresentative, idPessoaRepresentante]);

  const handleClearAllSelected = useCallback(() => {
    setSelectedCustomers([]);
  }, []);

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      // Uma chamada só: IdClientes vai no corpo do POST (sem limite de tamanho de URL) e o
      // backend já comita os inserts em lotes internamente (CriarClientes, Digital-API) —
      // não há mais motivo pra fatiar em lotes de 60 aqui no front.
      const response = await Api.post(
        "create-virtual-b2b-cliente",
        "showcaseB2B",
        {
          idVitrineVirtualB2b: Number(id),
          idClientes: selectedCustomers.toString(),
          idPessoaRepresentante,
        },
        true,
      );

      if (!response?.Success) {
        throw new Error(
          response?.ValidationMessage || "Falha ao publicar vitrine.",
        );
      }

      history.push("/admin/visualizar-vitrines-b2b");
      snackbar.showMessage(
        response.Result?.MessageResponse || "Vitrine publicada com sucesso!",
        { severity: "success" },
      );
    } catch (error) {
      console.error(error);
      snackbar.showMessage(
        error?.message || "Erro ao publicar vitrine. Tente novamente.",
        { severity: "error" },
      );
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <>
      <HeaderClientes
        section="Publicação da Vitrine – Público Alvo"
        subsection="Vitrine"
      />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <Card classes={{ root: classes.cardRoot }}>
          <CardContent>
            <Grid
              container
              alignItems="center"
              direction="row"
              justify="space-between"
            >
              <Typography variant="h2">
                Publicação da Vitrine – Público Alvo
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>

            {!isRepresentative && (
              <Grid item xs={12} sm={6} md={4} style={{ marginTop: "1.5rem" }}>
                <AutoCompleteField
                  label="Publicar em nome do representante"
                  options={representantesDisponiveis}
                  optionIdKey="IdPessoaRepresentante"
                  optionLabelKey="DisplayLabel"
                  value={selectedRepresentante}
                  onChange={handleRepresentanteChange}
                  loading={isLoadingRepresentantes}
                  required
                />
              </Grid>
            )}

            <RepresentativeFilters
              isLoading={isLoading}
              filters={filters}
              onFiltersChange={handleFiltersChange}
              grupoEconomicoOptions={grupoEconomicoOptions}
              razaoSocialOptions={razaoSocialOptions}
            />

            <RepresentativeSelectionList
              isLoading={isLoading}
              items={items}
              totalItems={totalItems}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              selectedIds={selectedIds}
              onToggleItem={handleToggleItem}
              onSelectVisible={selectVisible}
              onDeselectVisible={deselectVisible}
              onSelectAllResults={handleSelectAllResults}
              onDeselectAll={handleClearAllSelected}
              isItemSelectable={isCustomerSelectable}
              selectAllDialogTitle="Selecionar clientes"
              selectAllEntityLabel="clientes"
              emptyMessage="Nenhum cliente encontrado com os filtros aplicados."
              selectionFooter={
                selectedCustomers.length > 0 ? (
                  <Typography
                    variant="caption"
                    style={{
                      display: "block",
                      marginTop: 8,
                      color: themeColorsDesingSystem.gray.dark,
                    }}
                  >
                    {selectedCustomers.length} cliente(s) selecionado(s)
                  </Typography>
                ) : null
              }
            />

            <Box
              display="flex"
              width="100%"
              style={{ marginTop: "2rem", gap: "2rem" }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handlePublish}
                disabled={
                  isLoading ||
                  isPublishing ||
                  selectedCustomers.length === 0 ||
                  (!isRepresentative && !selectedRepresentante)
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                startIcon={
                  isPublishing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                {isPublishing ? "Publicando..." : "Publicar"}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => history.push("/admin/visualizar-vitrines-b2b")}
                disabled={isLoading || isPublishing}
              >
                Voltar
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </>
  );
}
